import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Target, Users, Zap, Award, Heart, TrendingUp } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'Every design decision is made with conversion and business growth in mind.'
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your success is our success. We prioritize communication and collaboration.'
    },
    {
      icon: Zap,
      title: 'Fast Delivery',
      description: 'We value your time and deliver quality work within promised timelines.'
    },
    {
      icon: Award,
      title: 'Premium Quality',
      description: 'We maintain the highest standards in design, development, and user experience.'
    },
    {
      icon: Heart,
      title: 'Passionate Team',
      description: 'We love what we do and it shows in every project we deliver.'
    },
    {
      icon: TrendingUp,
      title: 'Growth Mindset',
      description: 'We stay updated with the latest trends and technologies to serve you better.'
    }
  ];

  const team = [
    {
      name: 'Alex Rivera',
      role: 'Founder & Lead Designer',
      bio: '10+ years of experience in web design and UX optimization for high-growth startups.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'
    },
    {
      name: 'Sarah Chen',
      role: 'Senior Developer',
      bio: 'Full-stack expert specializing in performance optimization and scalable architecture.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop'
    },
    {
      name: 'Marcus Johnson',
      role: 'SEO Specialist',
      bio: 'Helping businesses rank higher with proven SEO strategies and technical optimization.',
      image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-amber-500/10 text-amber-400 border-amber-500/20">
            About Webrionix
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            We're on a Mission to Help
            <br />
            <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
              Businesses Grow Online
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Founded with a vision to make premium web design accessible to businesses of all sizes,
            Webrionix combines creativity with conversion optimization to deliver websites that truly work.
          </p>
        </div>

        {/* Story Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Webrionix was born from a simple observation: too many great businesses were being held back
                  by outdated, ineffective websites. We saw talented entrepreneurs, coaches, and small business
                  owners struggling with websites that didn't represent their true potential.
                </p>
                <p>
                  We founded Webrionix to bridge that gap. Our mission is to provide agency-quality web design
                  and development at prices that make sense for growing businesses. We believe every business
                  deserves a website that not only looks stunning but actually drives results.
                </p>
                <p>
                  Today, we've helped over 150 businesses transform their online presence and achieve measurable
                  growth. From solo entrepreneurs to established companies, we bring the same level of dedication
                  and expertise to every project.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              These principles guide every decision we make and every project we deliver
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card
                  key={index}
                  className="bg-slate-800/50 border-slate-700 hover:border-amber-500/50 transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {value.title}
                    </h3>
                    <p className="text-gray-400 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Team */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Working Remotely, Delivering Globally
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Based in New Delhi, India, we serve clients worldwide with dedication and expertise
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 md:p-12 border border-slate-800">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">By The Numbers</h2>
            <p className="text-gray-400">Our track record speaks for itself</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">150+</div>
              <div className="text-sm text-gray-400">Websites Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">98%</div>
              <div className="text-sm text-gray-400">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">2.5x</div>
              <div className="text-sm text-gray-400">Avg. Conversion Boost</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">5+</div>
              <div className="text-sm text-gray-400">Years in Business</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Work Together?
          </h2>
          <p className="text-gray-400 mb-8">
            Let's create something amazing for your business
          </p>
          <a href="/contact">
            <button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-8 py-3 rounded-lg shadow-lg shadow-amber-500/20 transition-all duration-300 hover:scale-105">
              Get in Touch
            </button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default About;
